import type {
  MediaImage,
  MediaImageSizeContent,
} from '@efdigital/ll-addon-data';

import type { MediaImageSizeType } from './models';
import type { Size } from './models';
/**
 * The sizing mode for an image.
 */
export const Sizing = {
  /**
   * Crops the image to fit the size. The image will be scaled to fit and
   * cover the size without distorting the aspect ratio, and any overflow
   * will be cropped.
   */
  Crop: 'crop',

  /**
   * Contains the image within the size. The image will not be cropped and
   * the aspect ratio of the image will be preserved.
   */
  Contain: 'contain',
} as const;

export interface SizePreset extends Size {
  sizing: (typeof Sizing)[keyof typeof Sizing];
}

export const ResourceIcon: SizePreset = {
  width: 360,
  height: 220,
  sizing: Sizing.Crop,
};

export const CohortIcon: SizePreset = {
  width: 498,
  height: 245,
  sizing: Sizing.Crop,
};

export const ModuleIcon: SizePreset = {
  width: 552,
  height: 284,
  sizing: Sizing.Crop,
};

export const ContactIcon: SizePreset = {
  width: 524,
  height: 320,
  sizing: Sizing.Crop,
};

export const EmailImage: SizePreset = {
  width: 600,
  height: 600,
  sizing: Sizing.Contain,
};

export const Thumbnail = {
  width: 208,
  height: 128,
};

export const ALL_SIZES: MediaImageSizeType[] = [
  'resource-icon',
  'cohort-icon',
  'module-icon',
  'contact-icon',
  'email-image',
];

export interface ImageContent {
  url?: string;
  contentType?: string;
  width?: number;
  height?: number;
  size?: string;
}

/**
 * Lookup a size variant from a media image record
 * @param record The media image record
 * @param size The size to lookup
 * @param customSize Custom dimensions (for 'custom' size)
 * @returns The image content for the requested size, or undefined
 */
export function lookupSize(
  record: MediaImage,
  size: MediaImageSizeType | 'original' | 'thumbnail',
  customSize?: Size
): ImageContent | undefined {
  switch (size) {
    case 'original':
      return record.original
        ? {
            url: record.original.url,
            contentType: record.original.contentType,
            width: record.original.width,
            height: record.original.height,
            size: 'original',
          }
        : undefined;

    case 'thumbnail':
      return record.thumbnailUrl
        ? {
            size: 'thumbnail',
            width: Thumbnail.width,
            height: Thumbnail.height,
            url: record.thumbnailUrl,
          }
        : undefined;

    case 'custom':
      if (!customSize) {
        return undefined;
      }
      return (record.sizes || []).find(
        (i: MediaImageSizeContent) =>
          i.size === size &&
          i.width === customSize.width &&
          i.height === customSize.height
      );
    default:
      return (record.sizes || []).find(
        (i: MediaImageSizeContent) => i.size === size
      );
  }
}

/**
 * Convert a size preset name to dimensions
 * @param size The size preset name
 * @returns The dimensions for the preset
 */
export function toSize(size: MediaImageSizeType): Size {
  switch (size) {
    case 'resource-icon':
      return ResourceIcon;
    case 'cohort-icon':
      return CohortIcon;
    case 'module-icon':
      return ModuleIcon;
    case 'contact-icon':
      return ContactIcon;
    case 'email-image':
      return EmailImage;
    case 'custom':
      throw new Error('Custom size requires explicit dimensions');
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Unknown size preset: ${size}`);
  }
}

/**
 * Get the recommended minimum dimensions for upload
 * @returns The minimum recommended dimensions
 */
export function getMinimumRecommendedSize(): Size {
  // Find the largest dimensions across all presets
  const sizes = [ResourceIcon, CohortIcon, ModuleIcon, ContactIcon, EmailImage];

  return {
    width: Math.max(...sizes.map((s) => s.width)),
    height: Math.max(...sizes.map((s) => s.height)),
  };
}

/**
 * Validate if an image size is sufficient for a target preset
 * @param imageWidth The actual image width
 * @param imageHeight The actual image height
 * @param targetSize The target size preset (optional)
 * @returns Validation result with warnings
 */
export function validateImageSize(
  imageWidth: number,
  imageHeight: number,
  targetSize?: MediaImageSizeType
): {
  isValid: boolean;
  warnings: string[];
  minimumRecommended: Size;
} {
  const warnings: string[] = [];
  const minimumRecommended = getMinimumRecommendedSize();

  // Check against target size if specified
  if (targetSize && targetSize !== 'custom') {
    const preset = toSize(targetSize);
    if (imageWidth < preset.width || imageHeight < preset.height) {
      warnings.push(
        `Image is smaller than recommended for ${targetSize} (${preset.width}x${preset.height}px). Quality may be affected.`
      );
    }
  }

  // Check against overall minimum
  if (
    imageWidth < minimumRecommended.width ||
    imageHeight < minimumRecommended.height
  ) {
    warnings.push(
      `Image is smaller than the recommended minimum (${minimumRecommended.width}x${minimumRecommended.height}px). Upload the largest size possible for best quality.`
    );
  }

  return {
    isValid: warnings.length === 0,
    warnings,
    minimumRecommended,
  };
}
